import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { RaisedButton, ListItem, List, Subheader, Checkbox } from 'material-ui';
import { Loading } from '../../common/components/loading.component';
import { UsersPicker } from './users-picker.container';
import { CoursesPicker } from './courses-picker.container';
import { FontAwesome } from '../../common/components/font-awesome';

const DEFAULT_PUBLICATION_TARGETS = [
  { id: 'all', name: 'All Participants', total: null },
  { id: 'users', name: 'By Participants', total: null },
  { id: 'courses', name: 'By Groups', total: null },
];

function getGroupDisplayName(group) {
  if (group.id === 'all') return 'All Participants';
  if (group.id === 'users') return 'By Participants';
  if (group.id === 'courses') return 'By Groups';
  if (group.name === 'All Students') return 'All Participants';
  if (group.name === 'By Courses') return 'By Groups';
  return group.name || 'Recipients';
}

function mergePublicationTargets(apiTargets) {
  const api = apiTargets || [];

  return DEFAULT_PUBLICATION_TARGETS.map(def => {
    const match = api.find(g => g.id === def.id);
    if (!match) return def;

    return {
      ...def,
      ...match,
      name: getGroupDisplayName(match),
    };
  });
}

function getGroupTotalLabel(group) {
  if (group.total === null || group.total === undefined) {
    return null;
  }

  return ` (Total ${group.total} participants)`;
}

export class TargetPickerFormView extends Component {
  static propTypes = {
    targets: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.any,
      publicationTargets: PropTypes.arrayOf(PropTypes.any)
    }).isRequired,
    updateTargets: PropTypes.func.isRequired,
    editEntityId: PropTypes.string,
    editEntity: PropTypes.object,
    currentLocation: PropTypes.any,
    navigate: PropTypes.func.isRequired,
    onChange: PropTypes.func,
    hideButtons: PropTypes.bool,
    disableAllUsers: PropTypes.bool,
    initialOpenPickers: PropTypes.arrayOf(PropTypes.string),
    groups: PropTypes.any
  };

  constructor(props) {
    super();

    this.state = {
      groups: props.groups || ['all'],
      closedPickers: props.initialOpenPickers || [],
      saving: false,
      error: null,
    };
  }

  isControlled() {
    return Array.isArray(this.props.groups);
  }

  getGroups() {
    return this.isControlled() ? this.props.groups : this.state.groups;
  }

  setGroups(groups) {
    if (this.isControlled()) {
      if (this.props.onChange) {
        this.props.onChange(groups);
      }
      return;
    }

    this.setState({ groups }, () => {
      if (this.props.onChange) {
        this.props.onChange(this.state.groups);
      }
    });
  }

  componentDidMount() {
    if (this.isControlled()) {
      return;
    }

    if (this.props.disableAllUsers) {
      this.setState({ groups: [] });
      return;
    }

    if (this.props.groups) {
      this.setState({ groups: this.props.groups });
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.editEntity) {
      const targets = nextProps.editEntity.targets || [];

      if (!this.isControlled()) {
        this.setState({ groups: targets });
      }
    }
  }

  countPicked(prefix) {
    return this.getGroups().filter(group => group.startsWith(prefix)).length;
  }

  handleGroupsChange(groupId, shouldInclude) {
    const currentGroups = this.getGroups();
    const hasGroup = currentGroups.includes(groupId);
    const nextInclude =
      shouldInclude !== undefined ? shouldInclude : !hasGroup;

    if (nextInclude === hasGroup) {
      return;
    }

    let groups;

    if (groupId === 'users' && !nextInclude) {
      groups = currentGroups.filter(
        id => !id.startsWith('user_') && id !== 'users',
      );
    } else if (groupId === 'courses' && !nextInclude) {
      groups = currentGroups.filter(
        id => !id.startsWith('course_') && id !== 'courses',
      );
    } else if (nextInclude) {
      groups = [...currentGroups, groupId];
      if (groupId === 'all') {
        groups = ['all'];
      } else {
        groups = groups.filter(id => id !== 'all');
      }
    } else {
      groups = currentGroups.filter(id => id !== groupId);
    }

    this.setGroups(groups);
  }

  toggleGroupClose(groupId) {
    if (!this.state.closedPickers.includes(groupId)) {
      this.setState({
        closedPickers: [...this.state.closedPickers, groupId]
      });
    } else {
      this.setState({
        closedPickers: this.state.closedPickers.filter(id => id !== groupId)
      });
    }
  }

  skip() {
    const entityType = this.props.currentLocation.split('/')[2];
    this.props.navigate(`/admin/${entityType}`);
  }

  save() {
    const entityType = this.props.currentLocation.split('/')[2];
    const itemId = this.props.editEntityId;
    if (!itemId) {
      this.setState({ error: 'Missing feed item id. Please refresh and try again.' });
      return;
    }

    this.setState({ saving: true, error: null });

    this.props
      .updateTargets(itemId, this.getGroups())
      .then(() => {
        this.setState({ saving: false }, () => {
          this.props.navigate(`/admin/${entityType}/platforms/${itemId}`);
        });
      })
      .catch(err => {
        // surface this to the UI; previously we only logged to console
        console.error(err);
        this.setState({
          saving: false,
          error:
            err?.message ||
            'Failed to save recipients. Please try again.',
        });
      });
  }

  renderUsersPicker() {
    const groups = this.getGroups();

    return (
      <UsersPicker
        fromMeetings={false}
        checkedItems={groups}
        onItemChange={(id, isChecked) => this.handleGroupsChange(id, isChecked)}
      />
    );
  }

  renderCoursesPicker() {
    const groups = this.getGroups();

    return (
      <CoursesPicker
        checkedItems={groups}
        onItemChange={(id, isChecked) => this.handleGroupsChange(id, isChecked)}
      />
    );
  }

  render() {
    if (this.props.targets.loading) {
      return <Loading />;
    }

    const publicationTargets = mergePublicationTargets(
      this.props.targets.publicationTargets,
    );
    const groups = this.getGroups();

    if (this.props.targets.error) {
      return (
        <div style={{ color: '#c62828', padding: '8px 0' }}>
          Could not load recipient groups. Please refresh and try again.
        </div>
      );
    }

    return (
      <List>
        <Subheader>Pick relevant groups:</Subheader>
        {this.state.error ? (
          <div style={{ color: '#c62828', padding: '8px 16px' }}>
            {this.state.error}
          </div>
        ) : null}
        {publicationTargets.map(group => {
          if (group.id === 'all' && this.props.disableAllUsers) {
            return null;
          }

          const isUsersGroup = group.id === 'users';
          const isCoursesGroup = group.id === 'courses';
          const pickedCount = isUsersGroup
            ? this.countPicked('user_')
            : isCoursesGroup
            ? this.countPicked('course_')
            : 0;
          const isChecked = isUsersGroup || isCoursesGroup
            ? pickedCount > 0
            : groups.includes(group.id);
          const total =
            group.total === null || group.total === undefined
              ? pickedCount > 0
                ? ` (${pickedCount} selected)`
                : null
              : getGroupTotalLabel(group);
          const name = getGroupDisplayName(group);
          const isPickerOpen = this.state.closedPickers.includes(group.id);
          const hasPicker = isUsersGroup || isCoursesGroup;

          return (
            <React.Fragment key={group.id}>
              <ListItem
                leftCheckbox={
                  <span
                    onClick={e => e.stopPropagation()}
                    onMouseDown={e => e.stopPropagation()}
                  >
                    <Checkbox
                      onCheck={(e, checked) => {
                        if (e && e.stopPropagation) {
                          e.stopPropagation();
                        }
                        this.handleGroupsChange(group.id, checked);
                      }}
                      checked={isChecked}
                    />
                  </span>
                }
                primaryText={
                  <span
                    style={{ cursor: hasPicker ? 'pointer' : 'default' }}
                    onClick={e => {
                      if (!hasPicker) return;
                      this.toggleGroupClose(group.id);
                      e.preventDefault();
                    }}
                  >
                    {name}
                    {total ? (
                      <span style={{ color: '#1976d2' }}>{total}</span>
                    ) : null}
                    {hasPicker ? (
                      <FontAwesome
                        style={{ marginLeft: 10 }}
                        name={isPickerOpen ? 'caret-up' : 'caret-down'}
                      />
                    ) : null}
                  </span>
                }
              />
              {isPickerOpen && isUsersGroup ? this.renderUsersPicker() : null}
              {isPickerOpen && isCoursesGroup ? this.renderCoursesPicker() : null}
            </React.Fragment>
          );
        })}
        {!this.props.hideButtons && (
          <ListItem>
            <RaisedButton
              onClick={() => this.skip()}
              style={{ marginRight: 30 }}
              label='Skip'
            />
            <RaisedButton
              onClick={() => this.save()}
              disabled={groups.length === 0 || this.state.saving}
              primary={true}
              label={this.state.saving ? 'Saving…' : 'Continue'}
            />
          </ListItem>
        )}
      </List>
    );
  }
}
